/**
 * modals/AssetClassSelectorModal.tsx
 * Asset class and taxonomy level selector modal for building fee hierarchy
 * 
 * PHASE 2: Modal shell with proper layouts and cascading selectors
 * Asset class dropdown with Market Segment and Sub-Asset Class cascading selectors
 * Add/Cancel buttons (non-functional)
 */

// 1. External dependencies
import React, { useState, useMemo } from 'react';
import { Layers, Plus, Info } from 'lucide-react';

// 2. Internal absolute imports
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, Select, SelectItem, Input, Card, CardBody } from '@enfomentis/ui';
import type { AssetClassType, MarketSegment, SubAssetClass } from '@/types/shared/AssetClass';
import { VALID_MARKET_SEGMENTS_BY_ASSET_CLASS, VALID_SUB_ASSET_CLASSES_BY_ASSET_CLASS, ASSET_CLASS_TYPE_DISPLAY_MAP } from '@/constants/assetClass';
import { formatAssetClassTypeDisplay } from '@/utils/assetClass';

// Existing hierarchy levels structure (matches FeeModalManager)
interface ExistingHierarchyLevels {
  assetClasses: string[];
  marketSegments: Record<string, string[]>; // assetClass -> marketSegments[]
  subAssetClasses: Record<string, string[]>; // "assetClass-marketSegment" -> subAssetClasses[]
}

/**
 * Props for the AssetClassSelectorModal component
 */
interface AssetClassSelectorModalProps {
  /** Whether the modal is open */
  isOpen: boolean;

  /** Handler for closing the modal */
  onClose: () => void;

  /** The hierarchy level being added */
  level: 'asset-class' | 'market-segment' | 'sub-asset-class';

  /** Optional parent context for market segment/sub-asset class selection */
  parentAssetClass?: AssetClassType;

  /** Existing hierarchy levels to filter out already-added options */
  existingHierarchyLevels?: ExistingHierarchyLevels;

  /** Handler for adding the selected level (Phase 2: non-functional) */
  onAdd: (selection: LevelSelection) => void;
}

/**
 * Level-specific selection data structure
 */
interface LevelSelection {
  level: 'asset-class' | 'market-segment' | 'sub-asset-class';
  value: AssetClassType | MarketSegment | SubAssetClass | string;
  customValue: string;
  label: string;
}

/**
 * Initial selection state
 */
const getInitialSelection = (level: 'asset-class' | 'market-segment' | 'sub-asset-class'): LevelSelection => ({
  level,
  value: '',
  customValue: '',
  label: ''
});

/**
 * Asset class options with descriptions
 */
const assetClassOptions = [
  { key: 'futures' as const satisfies AssetClassType, label: formatAssetClassTypeDisplay('futures' as const satisfies AssetClassType, ASSET_CLASS_TYPE_DISPLAY_MAP), description: 'Futures contracts and derivatives' },
  { key: 'cfd' as const satisfies AssetClassType, label: formatAssetClassTypeDisplay('cfd' as const satisfies AssetClassType, ASSET_CLASS_TYPE_DISPLAY_MAP), description: 'Contracts for difference' },
  { key: 'forex' as const satisfies AssetClassType, label: formatAssetClassTypeDisplay('forex' as const satisfies AssetClassType, ASSET_CLASS_TYPE_DISPLAY_MAP), description: 'Currency pairs and FX products' },
  { key: 'options' as const satisfies AssetClassType, label: formatAssetClassTypeDisplay('options' as const satisfies AssetClassType, ASSET_CLASS_TYPE_DISPLAY_MAP), description: 'Options contracts' },
  { key: 'stocks' as const satisfies AssetClassType, label: formatAssetClassTypeDisplay('stocks' as const satisfies AssetClassType, ASSET_CLASS_TYPE_DISPLAY_MAP), description: 'Stocks and ETFs' },
  { key: 'bonds' as const satisfies AssetClassType, label: formatAssetClassTypeDisplay('bonds' as const satisfies AssetClassType, ASSET_CLASS_TYPE_DISPLAY_MAP), description: 'Bonds and fixed income' },
  { key: 'crypto' as const satisfies AssetClassType, label: formatAssetClassTypeDisplay('crypto' as const satisfies AssetClassType, ASSET_CLASS_TYPE_DISPLAY_MAP), description: 'Cryptocurrency and digital assets' },
  { key: 'funds' as const satisfies AssetClassType, label: formatAssetClassTypeDisplay('funds' as const satisfies AssetClassType, ASSET_CLASS_TYPE_DISPLAY_MAP), description: 'Mutual funds and investment funds' }
] as const;


/**
 * Helper to get display name for market segment
 */
const getMarketSegmentDisplayName = (segment: string): string => {
  const displayNames: Record<string, string> = {
    'indices': 'Stock Indices',
    'commodities': 'Agricultural Commodities',
    'metals': 'Metals (Precious & Industrial)',
    'currencies': 'Currency Futures',
    'financials': 'Financial Products',
    'rates': 'Interest Rate Products',
    'energies': 'Energy Products',
    'meats': 'Livestock',
    'softs': 'Soft Commodities',
    'majors': 'Major Currency Pairs',
    'minors': 'Minor Currency Pairs',
    'exotics': 'Exotic Currency Pairs',
    'us-equities': 'US Stocks & ETFs',
    'eu-equities': 'European Equities',
    'emerging-markets': 'Emerging Markets',
    'international': 'International Markets',
    'government': 'Government Bonds',
    'corporate': 'Corporate Bonds',
    'municipal': 'Municipal Bonds',
    'digital-assets': 'Digital Assets',
    'other': 'Other/Custom'
  };
  return displayNames[segment] || segment;
};

/**
 * Helper to get display name for sub-asset class
 */
const getSubAssetClassDisplayName = (subAsset: string): string => {
  const displayNames: Record<string, string> = {
    'micro': 'Micro Contracts',
    'mini': 'Mini Contracts', 
    'full-size': 'Full Size Contracts',
    'weekly': 'Weekly Options',
    'monthly': 'Monthly Options',
    'quarterly': 'Quarterly Options',
    'large-cap': 'Large Cap',
    'mid-cap': 'Mid Cap',
    'small-cap': 'Small Cap',
    'growth': 'Growth',
    'value': 'Value',
    'dividend': 'Dividend Focused',
    'short-term': 'Short Term',
    'intermediate': 'Intermediate Term',
    'long-term': 'Long Term',
    'high-yield': 'High Yield',
    'investment-grade': 'Investment Grade',
    'spot': 'Spot Trading',
    'perpetual': 'Perpetual Contracts',
    'other': 'Other/Custom'
  };
  return displayNames[subAsset] || subAsset;
};

/**
 * AssetClassSelectorModal component for selecting specific hierarchy levels
 */
export const AssetClassSelectorModal: React.FC<AssetClassSelectorModalProps> = ({
  isOpen,
  onClose,
  level,
  parentAssetClass,
  existingHierarchyLevels,
  onAdd
}) => {
  const [selection, setSelection] = useState<LevelSelection>(() => getInitialSelection(level));
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset selection when level changes
  React.useEffect(() => {
    setSelection(getInitialSelection(level));
  }, [level]);

  // Filter available options based on existing hierarchy
  const availableAssetClassOptions = useMemo(() => {
    if (!existingHierarchyLevels) return assetClassOptions;

    return assetClassOptions.filter(option =>
      !existingHierarchyLevels.assetClasses.includes(option.key)
    );
  }, [existingHierarchyLevels]);

  const availableMarketSegmentOptions = useMemo(() => {
    if (!existingHierarchyLevels || !parentAssetClass) {
      return VALID_MARKET_SEGMENTS_BY_ASSET_CLASS[parentAssetClass || 'futures'] || [];
    }

    const validSegments = VALID_MARKET_SEGMENTS_BY_ASSET_CLASS[parentAssetClass] || [];
    const existingSegments = existingHierarchyLevels.marketSegments[parentAssetClass] || [];

    return validSegments.filter(segment => !existingSegments.includes(segment));
  }, [existingHierarchyLevels, parentAssetClass]);

  const availableSubAssetClassOptions = useMemo(() => {
    if (!parentAssetClass) {
      return [];
    }

    const validSubAssets = VALID_SUB_ASSET_CLASSES_BY_ASSET_CLASS[parentAssetClass] || [];

    if (!existingHierarchyLevels || selection.level !== 'sub-asset-class') {
      return validSubAssets;
    }

    // For sub-asset class, we need both parent asset class and market segment
    // The current selection.value should be the market segment for sub-asset class selection
    const marketSegment = selection.value;
    if (!marketSegment) {
      return validSubAssets;
    }

    const existingKey = `${parentAssetClass}-${marketSegment}`;
    const existingSubAssets = existingHierarchyLevels.subAssetClasses[existingKey] || [];

    return validSubAssets.filter(subAsset => !existingSubAssets.includes(subAsset));
  }, [existingHierarchyLevels, parentAssetClass, selection]);

  // Handle selection updates
  const updateSelection = (value: string, customValue: string = ''): void => {
    const label = getLabelForValue(value, customValue);
    setSelection({
      level,
      value,
      customValue,
      label
    });
  };

  // Get label for selected value
  const getLabelForValue = (value: string, customValue?: string): string => {
    if (value === 'other' && customValue) {
      return customValue;
    }
    
    switch (level) {
      case 'asset-class':
        return assetClassOptions.find(opt => opt.key === value)?.label || value;
      case 'market-segment':
        return getMarketSegmentDisplayName(value);
      case 'sub-asset-class':
        return getSubAssetClassDisplayName(value);
      default:
        return value;
    }
  };

  // Get available options based on level and parent context (filtered by existing hierarchy)
  const availableOptions = useMemo(() => {
    switch (level) {
      case 'asset-class':
        return availableAssetClassOptions;
      case 'market-segment': {
        if (!parentAssetClass) return [];
        return availableMarketSegmentOptions.map(segment => ({
          key: segment,
          label: getMarketSegmentDisplayName(segment),
          description: `Market segment for ${parentAssetClass}`
        }));
      }
      case 'sub-asset-class': {
        if (!parentAssetClass) return [];
        return availableSubAssetClassOptions.map(subAsset => ({
          key: subAsset,
          label: getSubAssetClassDisplayName(subAsset),
          description: `Sub-category for ${parentAssetClass}`
        }));
      }
      default:
        return [];
    }
  }, [level, parentAssetClass, availableAssetClassOptions, availableMarketSegmentOptions, availableSubAssetClassOptions]);

  // Get modal configuration based on level
  const getModalConfig = () => {
    switch (level) {
      case 'asset-class':
        return {
          title: 'Add Asset Class',
          description: 'Select a primary trading instrument type',
          placeholder: 'Select asset class'
        };
      case 'market-segment':
        return {
          title: 'Add Market Segment',
          description: `Select a market segment${parentAssetClass ? ` for ${parentAssetClass}` : ''}`,
          placeholder: 'Select market segment'
        };
      case 'sub-asset-class':
        return {
          title: 'Add Sub-Asset Class', 
          description: `Select a sub-category${parentAssetClass ? ` for ${parentAssetClass}` : ''}`,
          placeholder: 'Select sub-asset class'
        };
      default:
        return {
          title: 'Add Level',
          description: 'Select an option',
          placeholder: 'Select option'
        };
    }
  };

  // Check if selection is valid
  const isSelectionValid = useMemo(() => {
    return selection.value !== '' && (selection.value !== 'other' || selection.customValue?.trim() !== '');
  }, [selection]);

  // Handle add action (Phase 2: placeholder)
  const handleAdd = (): void => {
    setIsSubmitting(true);
    
    // Simulate async operation
    setTimeout(() => {
      onAdd(selection);
      setIsSubmitting(false);
      onClose();
      setSelection(getInitialSelection(level)); // Reset form
    }, 1000);
  };

  // Handle cancel action
  const handleCancel = (): void => {
    setSelection(getInitialSelection(level)); // Reset form
    onClose();
  };

  const modalConfig = getModalConfig();

  return (
    <Modal 
      isOpen={isOpen} 
      onOpenChange={onClose}
      size="lg"
      scrollBehavior="inside"
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <Layers className="w-5 h-5 text-primary" />
                <h3 className="font-semibold">{modalConfig.title}</h3>
              </div>
              <p className="text-sm text-text-secondary font-normal">
                {modalConfig.description}
              </p>
            </ModalHeader>

            <ModalBody className="gap-6">
              {/* Level-specific Selection */}
              <div className="space-y-4">
                <Select
                  label={modalConfig.title.replace('Add ', '')}
                  variant="underlined"
                  size="sm"
                  placeholder={modalConfig.placeholder}
                  selectedKeys={selection.value ? [selection.value] : []}
                  onSelectionChange={(keys) => {
                    const selectedValue = Array.from(keys)[0] as string;
                    updateSelection(selectedValue || '');
                  }}
                  classNames={{
                      listboxWrapper: "data-[hover=true]:bg-primary-50\
                       data-[selectable=true]:focus:bg-primary-50\
                        [&_[data-selected=true]]:!bg-primary-50\
                         [&_[aria-selected=true]]:!bg-primary-50\
                          [&_[data-selected=false]]:!bg-transparent\
                           [&_[aria-selected=false]]:!bg-transparent",
                    }}
                >
                  {[
                    ...availableOptions.map((item) => (
                      <SelectItem 
                        key={item.key} 
                        description={item.description}
                      >
                        {item.label}
                      </SelectItem>
                    )),
                    ...((level === 'market-segment' || level === 'sub-asset-class') && !availableOptions.some(opt => opt.key === 'other')
                      ? [<SelectItem key="other">Custom / Other</SelectItem>]
                      : []
                    )
                  ]}
                </Select>
                
                {selection.value === 'other' && (
                  <Input
                    label={`Custom ${modalConfig.title.replace('Add ', '')}`}
                    placeholder={`Enter custom ${level.replace('-', ' ')} name`}
                    value={selection.customValue || ''}
                    onValueChange={(value) => updateSelection('other', value)}
                    description={`Specify your custom ${level.replace('-', ' ')} name`}
                  />
                )}
              </div>

              {/* Preview Section */}
              {isSelectionValid && (
                <Card className="bg-primary/5 border border-primary/20">
                  <CardBody className="gap-3">
                    <div className="flex items-center gap-2">
                      <Info className="w-4 h-4 text-primary" />
                      <h4 className="font-semibold text-primary">Preview</h4>
                    </div>
                    <div className="pl-6">
                      <p className="text-sm text-text-primary">
                        <strong>{selection.label}</strong> ({level.replace('-', ' ')})
                      </p>
                      <p className="text-xs text-text-secondary mt-1">
                        This {level.replace('-', ' ')} will be added to your fee configuration
                      </p>
                    </div>
                  </CardBody>
                </Card>
              )}
            </ModalBody>

            <ModalFooter>
              <Button 
                color="danger" 
                variant="light" 
                onPress={handleCancel}
                isDisabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button 
                color="primary" 
                onPress={handleAdd}
                isLoading={isSubmitting}
                isDisabled={!isSelectionValid}
                startContent={!isSubmitting ? <Plus className="w-4 h-4" /> : undefined}
              >
                {isSubmitting ? 'Adding...' : `Add ${modalConfig.title.replace('Add ', '')}`}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};